import Col from 'antd/es/grid/col';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane } from '@fortawesome/free-regular-svg-icons/faPaperPlane';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import { useLanguage } from '@/entities/locale';
import { buildStaticAssetUrl, deviceSizes, env } from '@/shared/config';
import { Button } from './Button';

export interface FooterHighlightTarget {
  id: string;
  href: string;
  footerMessageId?: string;
}

interface FooterProps {
  createHref: string;
  forumHref: string;
  highlightTargets: ReadonlyArray<FooterHighlightTarget>;
}

const EMAIL = 'info@creaticode.com';

const UpperFooterWrapper = styled.section`
  background-color: var(--surface-mint);
`;

const UpperFooterHero = styled.div`
  background-image: url(${buildStaticAssetUrl('images', 'upper_footer_bg.png')});
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  border-radius: 6px;
  padding: 5rem 0;
  text-align: center;
`;

const UpperFooterTitle = styled.h2`
  color: var(--green-700);
  font-size: clamp(30px, 3vw, 40px);
  font-weight: var(--weight-bold);
  line-height: 1.28;
  margin: 0 0 18px;
`;

const FooterWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  padding: 46px 0 42px;
`;

const FooterContainer = styled.div`
  column-gap: 56px;
  display: flex;
  flex-wrap: wrap;
  height: 100%;
  justify-content: flex-start;
  row-gap: 36px;
  width: 100%;
`;

const MenuContainer = styled(Col)`
  @media only screen and (max-width: ${deviceSizes.md}) {
    padding-bottom: 20px;
  }
`;

const FooterMenuWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;

  @media screen and (max-width: ${deviceSizes.md}) {
    justify-content: center;
  }
`;

const FooterMenuTitle = styled.h3`
  color: var(--green-700);
  font-size: var(--text-base);
  font-weight: var(--weight-bold);
  line-height: 1.5;
  margin: 0;
  padding-bottom: 14px;
  text-align: left;
  text-transform: uppercase;

  @media screen and (max-width: ${deviceSizes.md}) {
    text-align: center;
  }
`;

const FooterMenuItemList = styled.ul`
  display: block;
  list-style-type: none;
  margin: 0;
  padding-left: 0;
`;

const FooterMenuItemLink = styled.a`
  min-block-size: 44px;
  padding-block: 9px;

  &:active {
    opacity: 0.8;
  }
  color: var(--text-body);
  display: block;
  text-decoration: none;
  transition: color var(--duration-fast) ease;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      color: var(--green-700);
    }
  }
`;

const FooterMenuItemContent = styled.span`
  color: inherit;
  display: block;
  font-size: 13px;
  line-height: 1.9;
  margin-bottom: 0;
  text-align: left;
  word-break: break-all;

  @media only screen and (max-width: ${deviceSizes.md}) {
    text-align: center;
  }
`;

const ContactLink = styled.a`
  min-block-size: 44px;

  &:active {
    opacity: 0.8;
  }
  align-items: center;
  color: var(--text-body);
  display: inline-flex;
  font-size: 13px;
  gap: 4px;
  line-height: 1.9;
  margin-left: -7px;
  text-decoration: none;
  transition: color var(--duration-fast) ease;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      color: var(--green-700);
    }
  }

  @media only screen and (max-width: ${deviceSizes.md}) {
    justify-content: center;
    margin-left: 0;
  }
`;

const CopyRightWrapper = styled.div`
  border-top: 1px solid var(--hairline);
  margin: 0;
  padding: 20px 0 36px;
  width: 100%;
`;

const CopyRightText = styled.p`
  color: var(--text-body);
  font-size: 13px;
  line-height: var(--leading-relaxed);
  margin: 0;
  text-align: left;
  white-space: pre-line;

  @media only screen and (max-width: ${deviceSizes.md}) {
    text-align: center;
  }
`;

const BeianLink = styled.a`
  align-items: center;
  display: inline-flex;
  min-block-size: 44px;

  &:active {
    opacity: 0.8;
  }
  color: var(--ink-800);
  margin: 0 4px;
  text-decoration: none;
`;

const BeianIcon = styled.img`
  margin-right: 4px;
  width: 14px;
`;

function FooterMenuItem({ href, title }: { href: string; title: string }) {
  return (
    <li>
      <FooterMenuItemLink href={href}>
        <FooterMenuItemContent>{title}</FooterMenuItemContent>
      </FooterMenuItemLink>
    </li>
  );
}

const UpperFooterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
`;

function UpperFooter({ createHref }: Pick<FooterProps, 'createHref'>) {
  return (
    <UpperFooterWrapper>
      <UpperFooterRow>
        <Col xxl={14} lg={18} xs={24}>
          <UpperFooterHero>
            <UpperFooterTitle>
              <FormattedMessage id="UpperFooter.title" />
            </UpperFooterTitle>
            <Button href={createHref} variant="cta" wobble>
              <FormattedMessage id="UpperFooter.joinButton" />
            </Button>
          </UpperFooterHero>
        </Col>
      </UpperFooterRow>
    </UpperFooterWrapper>
  );
}

function HighlightMenu({ highlightTargets }: Pick<FooterProps, 'highlightTargets'>) {
  const intl = useIntl();

  return (
    <MenuContainer xl={4} lg={5} md={6} xs={12}>
      <FooterMenuWrapper>
        <Col span={24}>
          <FooterMenuTitle>
            <FormattedMessage id="Footer.Title.highlight" />
          </FooterMenuTitle>
          <FooterMenuItemList>
            {highlightTargets.flatMap((item) => {
              if (!item.footerMessageId) {
                return [];
              }

              return [<FooterMenuItem key={item.id} href={item.href} title={intl.formatMessage({ id: item.footerMessageId })} />];
            })}
          </FooterMenuItemList>
        </Col>
      </FooterMenuWrapper>
    </MenuContainer>
  );
}

function ContactMenu() {
  return (
    <MenuContainer xl={4} lg={5} md={6} xs={12}>
      <FooterMenuWrapper>
        <Col span={24}>
          <FooterMenuTitle>
            <FormattedMessage id="Footer.Title.contact" />
          </FooterMenuTitle>
          <ContactLink href={`mailto:${EMAIL}`}>
            <FontAwesomeIcon icon={faPaperPlane} />
            {EMAIL}
          </ContactLink>
        </Col>
      </FooterMenuWrapper>
    </MenuContainer>
  );
}

function CommunityMenu({ forumHref }: Pick<FooterProps, 'forumHref'>) {
  const intl = useIntl();

  return (
    <MenuContainer xl={4} lg={5} md={6} xs={12}>
      <FooterMenuWrapper>
        <Col span={24}>
          <FooterMenuTitle>
            <FormattedMessage id="Footer.Title.community" />
          </FooterMenuTitle>
          <FooterMenuItemList>
            <FooterMenuItem href={forumHref} title={intl.formatMessage({ id: 'Footer.Highlight.forum' })} />
          </FooterMenuItemList>
        </Col>
      </FooterMenuWrapper>
    </MenuContainer>
  );
}

const MissionContainer = styled(Col)`
  margin-left: auto;
  max-width: 380px;

  @media only screen and (max-width: ${deviceSizes.md}) {
    margin-left: 0;
    max-width: none;
    padding-bottom: 20px;
  }
`;

const MissionText = styled.p`
  color: var(--text-muted);
  font-size: 13px;
  line-height: var(--leading-relaxed);
  margin: 0;
  text-align: left;

  @media only screen and (max-width: ${deviceSizes.md}) {
    text-align: center;
  }
`;

function MissionMenu() {
  return (
    <MissionContainer xxl={7} xl={7} lg={8} md={24} xs={24}>
      <FooterMenuTitle>
        <FormattedMessage id="Footer.Title.mission" />
      </FooterMenuTitle>
      <MissionText>
        <FormattedMessage id="Footer.mission" />
      </MissionText>
    </MissionContainer>
  );
}

function CopyRight() {
  const { lang } = useLanguage();
  const companyName = env.isCn
    ? lang === 'zh'
      ? '途零教育科技（北京）有限公司'
      : 'Beijing Tu Ling Education Technology Inc.'
    : lang === 'zh'
      ? '提升科技有限公司'
      : 'Uplifting Technology, Inc.';

  return (
    <CopyRightText>
      Copyright © {new Date().getFullYear()} <strong>{companyName}</strong>
      {env.isCn ? (
        <>
          <br />
          <BeianLink href="http://www.beian.gov.cn/portal/registerSystemInfo?recordcode=11010802034089" rel="noreferrer" target="_blank">
            <BeianIcon alt="Beian record icon" src={buildStaticAssetUrl('images', 'beian.png')} />
            京公网安备 11010802034089 号
          </BeianLink>
          |
          <BeianLink href="https://beian.miit.gov.cn/" rel="noreferrer" target="_blank">
            京ICP备 2020033926 号
          </BeianLink>
          <br />
        </>
      ) : (
        '  '
      )}
      <FormattedMessage id="Footer.CopyRight" />
    </CopyRightText>
  );
}

export function Footer({ createHref, forumHref, highlightTargets }: FooterProps) {
  return (
    <footer>
      <UpperFooter createHref={createHref} />
      <FooterWrapper>
        <Col xxl={14} lg={18} md={20} xs={22}>
          <FooterContainer>
            <HighlightMenu highlightTargets={highlightTargets} />
            <ContactMenu />
            <CommunityMenu forumHref={forumHref} />
            <MissionMenu />
          </FooterContainer>
          <CopyRightWrapper>
            <CopyRight />
          </CopyRightWrapper>
        </Col>
      </FooterWrapper>
    </footer>
  );
}
